import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Company, 
  CompanyMember, 
  UserInvitation, 
  UserProfile, 
  CompanyContract,
  CompanyStats,
  CompanyActivity,
  UserRole,
  MemberStatus
} from '../types/company';

export class CompanyService {
  // Company Management
  static async createCompany(companyData: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>): Promise<Company> {
    try {
      const companyRef = await addDoc(collection(db, 'companies'), {
        ...companyData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      // Create company stats
      await addDoc(collection(db, 'companyStats'), {
        companyId: companyRef.id,
        totalContracts: 0,
        activeContracts: 0,
        completedContracts: 0,
        totalUsers: 1,
        activeUsers: 1,
        averageCompletionTime: 0,
        lastUpdated: serverTimestamp(),
      });
      
      // Fetch and return the created company
      const createdCompany = await this.getCompany(companyRef.id);
      if (!createdCompany) {
        throw new Error('Failed to retrieve created company');
      }
      return createdCompany;
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  }

  static async getCompany(companyId: string): Promise<Company | null> {
    try {
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        return { id: companyDoc.id, ...companyDoc.data() } as Company;
      }
      return null;
    } catch (error) {
      console.error('Error getting company:', error);
      throw error;
    }
  }

  static async getAllCompanies(): Promise<Company[]> {
    try {
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      return companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Company[];
    } catch (error) {
      console.error('Error getting all companies:', error);
      throw error;
    }
  }

  static async updateCompany(companyId: string, updates: Partial<Company>): Promise<void> {
    try {
      await updateDoc(doc(db, 'companies', companyId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  }

  static async deleteCompany(companyId: string): Promise<void> {
    try {
      // Delete company and all related data
      await deleteDoc(doc(db, 'companies', companyId));
      await deleteDoc(doc(db, 'companyStats', companyId));
      
      // Delete all company members
      const membersQuery = query(collection(db, 'companyMembers'), where('companyId', '==', companyId));
      const membersSnapshot = await getDocs(membersQuery);
      const deletePromises = membersSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Delete all company contracts
      const contractsQuery = query(collection(db, 'contracts'), where('companyId', '==', companyId));
      const contractsSnapshot = await getDocs(contractsQuery);
      const contractDeletePromises = contractsSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(contractDeletePromises);
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  }

  // Company Members Management
  static async addCompanyMember(memberData: Omit<CompanyMember, 'id'>): Promise<string> {
    try {
      const memberRef = await addDoc(collection(db, 'companyMembers'), memberData);
      return memberRef.id;
    } catch (error) {
      console.error('Error adding company member:', error);
      throw error;
    }
  }

  static async getCompanyMembers(companyId: string): Promise<CompanyMember[]> {
    try {
      console.log('Loading members for company:', companyId);
      
      // Use simple query without orderBy to avoid index issues
      const membersQuery = query(
        collection(db, 'companyMembers'),
        where('companyId', '==', companyId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      
      const members = membersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CompanyMember));
      console.log('Found members:', members.length, members);
      
      // Sort manually by joinedAt if available, otherwise by invitedAt
      members.sort((a, b) => {
        const aDate = a.joinedAt || a.invitedAt || '';
        const bDate = b.joinedAt || b.invitedAt || '';
        return bDate.localeCompare(aDate); // Descending order
      });
      
      return members;
    } catch (error) {
      console.error('Error getting company members:', error);
      throw error;
    }
  }

  static async getUserCompanies(userId: string): Promise<Company[]> {
    try {
      const membersQuery = query(
        collection(db, 'companyMembers'),
        where('userId', '==', userId),
        where('status', '==', 'active')
      );
      const membersSnapshot = await getDocs(membersQuery);
      
      const companyIds = membersSnapshot.docs.map(doc => doc.data().companyId);
      const companies: Company[] = [];
      
      for (const companyId of companyIds) {
        const company = await this.getCompany(companyId);
        if (company) companies.push(company);
      }
      
      return companies;
    } catch (error) {
      console.error('Error getting user companies:', error);
      throw error;
    }
  }

  static async updateMemberRole(memberId: string, role: UserRole, permissions: any): Promise<void> {
    try {
      await updateDoc(doc(db, 'companyMembers', memberId), {
        role,
        permissions,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating member role:', error);
      throw error;
    }
  }

  static async removeCompanyMember(companyId: string, userId: string): Promise<void> {
    try {
      const membersQuery = query(
        collection(db, 'companyMembers'),
        where('companyId', '==', companyId),
        where('userId', '==', userId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      
      if (!membersSnapshot.empty) {
        await deleteDoc(membersSnapshot.docs[0].ref);
      }
    } catch (error) {
      console.error('Error removing company member:', error);
      throw error;
    }
  }

  static async updateCompanyMember(companyId: string, userId: string, updates: Partial<CompanyMember>): Promise<void> {
    try {
      const membersQuery = query(
        collection(db, 'companyMembers'),
        where('companyId', '==', companyId),
        where('userId', '==', userId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      
      if (!membersSnapshot.empty) {
        await updateDoc(membersSnapshot.docs[0].ref, {
          ...updates,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating company member:', error);
      throw error;
    }
  }

  // User Invitations
  static async createUserInvitation(invitationData: Omit<UserInvitation, 'id'>): Promise<string> {
    try {
      const invitationRef = await addDoc(collection(db, 'userInvitations'), invitationData);
      return invitationRef.id;
    } catch (error) {
      console.error('Error creating user invitation:', error);
      throw error;
    }
  }

  static async inviteUserToCompany(data: {
    companyId: string;
    email: string;
    role: UserRole;
    invitedBy: string;
  }): Promise<string> {
    try {
      const invitationData: Omit<UserInvitation, 'id'> = {
        companyId: data.companyId,
        email: data.email,
        role: data.role,
        invitedBy: data.invitedBy,
        invitedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        status: 'pending',
        token: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
      };
      return await this.createUserInvitation(invitationData);
    } catch (error) {
      console.error('Error inviting user to company:', error);
      throw error;
    }
  }

  static async getPendingInvitations(companyId: string): Promise<UserInvitation[]> {
    try {
      const invitationsQuery = query(
        collection(db, 'userInvitations'),
        where('companyId', '==', companyId),
        where('status', '==', 'pending'),
        orderBy('invitedAt', 'desc')
      );
      const invitationsSnapshot = await getDocs(invitationsQuery);
      return invitationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserInvitation));
    } catch (error) {
      console.error('Error getting pending invitations:', error);
      throw error;
    }
  }

  static async acceptInvitation(invitationId: string, userId: string): Promise<void> {
    try {
      const invitationDoc = await getDoc(doc(db, 'userInvitations', invitationId));
      if (!invitationDoc.exists()) throw new Error('Invitation not found');
      
      const invitation = invitationDoc.data() as UserInvitation;
      
      // Update invitation status
      await updateDoc(doc(db, 'userInvitations', invitationId), {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
      });
      
      // Add user to company members
      await this.addCompanyMember({
        companyId: invitation.companyId,
        userId,
        role: invitation.role,
        status: 'active',
        invitedBy: invitation.invitedBy,
        invitedAt: invitation.invitedAt,
        joinedAt: new Date().toISOString(),
        permissions: this.getDefaultPermissions(invitation.role),
      });
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  }

  // User Profile Management
  static async createUserProfile(profileData: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const profileRef = await addDoc(collection(db, 'userProfiles'), {
        ...profileData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return profileRef.id;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  static async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const profileDoc = await getDoc(doc(db, 'userProfiles', userId));
      if (profileDoc.exists()) {
        return { id: profileDoc.id, ...profileDoc.data() } as UserProfile;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  static async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      await updateDoc(doc(db, 'userProfiles', userId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Company Contracts (extended from existing contract system)
  static async getCompanyContracts(companyId: string): Promise<CompanyContract[]> {
    try {
      const contractsQuery = query(
        collection(db, 'contracts'),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc')
      );
      const contractsSnapshot = await getDocs(contractsQuery);
      return contractsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CompanyContract));
    } catch (error) {
      console.error('Error getting company contracts:', error);
      throw error;
    }
  }

  // Company Statistics
  static async getCompanyStats(companyId: string): Promise<CompanyStats | null> {
    try {
      const statsDoc = await getDoc(doc(db, 'companyStats', companyId));
      if (statsDoc.exists()) {
        const data = statsDoc.data();
        return {
          id: statsDoc.id,
          companyId: data.companyId,
          totalContracts: data.totalContracts,
          activeContracts: data.activeContracts,
          completedContracts: data.completedContracts,
          totalUsers: data.totalUsers,
          activeUsers: data.activeUsers,
          averageCompletionTime: data.averageCompletionTime,
          lastUpdated: data.lastUpdated
        } as CompanyStats;
      }
      return null;
    } catch (error) {
      console.error('Error getting company stats:', error);
      throw error;
    }
  }

  // Activity Logging
  static async logCompanyActivity(activityData: Omit<CompanyActivity, 'id'>): Promise<void> {
    try {
      await addDoc(collection(db, 'companyActivities'), activityData);
    } catch (error) {
      console.error('Error logging company activity:', error);
      throw error;
    }
  }

  // Helper Methods
  static getDefaultPermissions(role: UserRole): any {
    const permissions = {
      canCreateContracts: false,
      canEditAllContracts: false,
      canDeleteContracts: false,
      canManageUsers: false,
      canManageCompany: false,
      canViewAnalytics: false,
      canExportData: false,
    };

    switch (role) {
      case 'owner':
        return {
          ...permissions,
          canCreateContracts: true,
          canEditAllContracts: true,
          canDeleteContracts: true,
          canManageUsers: true,
          canManageCompany: true,
          canViewAnalytics: true,
          canExportData: true,
        };
      case 'admin':
        return {
          ...permissions,
          canCreateContracts: true,
          canEditAllContracts: true,
          canDeleteContracts: true,
          canManageUsers: true,
          canViewAnalytics: true,
          canExportData: true,
        };
      case 'manager':
        return {
          ...permissions,
          canCreateContracts: true,
          canEditAllContracts: true,
          canViewAnalytics: true,
          canExportData: true,
        };
      case 'user':
        return {
          ...permissions,
          canCreateContracts: true,
        };
      default:
        return permissions;
    }
  }

  // Real-time listeners
  static subscribeToCompanyMembers(companyId: string, callback: (members: CompanyMember[]) => void) {
    const membersQuery = query(
      collection(db, 'companyMembers'),
      where('companyId', '==', companyId),
      where('status', '==', 'active')
    );
    
    return onSnapshot(membersQuery, (snapshot) => {
      const members = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CompanyMember));
      callback(members);
    });
  }

  static subscribeToCompanyContracts(companyId: string, callback: (contracts: CompanyContract[]) => void) {
    const contractsQuery = query(
      collection(db, 'contracts'),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(contractsQuery, (snapshot) => {
      const contracts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CompanyContract));
      callback(contracts);
    });
  }
}
